import { apiRequest } from "@/shared/lib/apiClient";
import type { ChatMessage, MessageThread } from "../types";

type MessagingApi = {
  fetchThreads: () => Promise<MessageThread[]>;
  fetchMessages: (threadId: string) => Promise<ChatMessage[]>;
  sendMessage: (threadId: string, content: string) => Promise<ChatMessage>;
  markRead: (threadId: string) => Promise<void>;
};

async function fetchThreads(basePath: string): Promise<MessageThread[]> {
  const response = await apiRequest<{ threads: MessageThread[] }>(`${basePath}/threads`, {
    auth: true,
  });
  return response.threads ?? [];
}

async function fetchMessages(basePath: string, threadId: string): Promise<ChatMessage[]> {
  const response = await apiRequest<{ messages: ChatMessage[] }>(
    `${basePath}/threads/${threadId}/messages`,
    { auth: true },
  );
  return response.messages ?? [];
}

async function sendMessage(
  basePath: string,
  threadId: string,
  content: string,
): Promise<ChatMessage> {
  const response = await apiRequest<{ message: ChatMessage }>(
    `${basePath}/threads/${threadId}/messages`,
    {
      method: "POST",
      auth: true,
      body: { content },
    },
  );
  return response.message;
}

async function markRead(basePath: string, threadId: string): Promise<void> {
  await apiRequest<{ message: string }>(`${basePath}/threads/${threadId}/read`, {
    method: "POST",
    auth: true,
  });
}

function createMessagingApi(basePath: string): MessagingApi {
  return {
    fetchThreads: () => fetchThreads(basePath),
    fetchMessages: (threadId) => fetchMessages(basePath, threadId),
    sendMessage: (threadId, content) => sendMessage(basePath, threadId, content),
    markRead: (threadId) => markRead(basePath, threadId),
  };
}

export const companyMessagingApi = createMessagingApi("/company/messaging");
export const adminMessagingApi = createMessagingApi("/admin/messaging");
export const hospitalMessagingApi = createMessagingApi("/hospital/messaging");
export const auditorMessagingApi = createMessagingApi("/auditor/messaging");

export async function fetchCompanyMessagingThreads(): Promise<MessageThread[]> {
  return companyMessagingApi.fetchThreads();
}

export async function fetchCompanyThreadMessages(threadId: string): Promise<ChatMessage[]> {
  return companyMessagingApi.fetchMessages(threadId);
}

export async function sendCompanyThreadMessage(
  threadId: string,
  content: string,
): Promise<ChatMessage> {
  return companyMessagingApi.sendMessage(threadId, content);
}

export async function markCompanyThreadRead(threadId: string): Promise<void> {
  return companyMessagingApi.markRead(threadId);
}
